import { useParams } from "react-router-dom";
import { ViewLayout } from "../../films/sections/view.layout";
import { ProjectKind } from "../../../enums/ProjectKind";
import { styled } from "styled-components";
import { Card, Tabs, Spin } from "antd";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Row } from "../../../components/ui/grid/row.component";
import { Pill } from "../../../components/ui/pill/pill.component";
import { colors } from "../../../theme/colors";
import _ from "lodash";
import { useEffect, useState } from "react";
import { axios } from "../../../api/axios";
import { SeriesProfile } from "../../../models/series-profile.model";
import { handleResponse } from "../../../utils/functions/http/handleResponse";

export const ViewEpisodes = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<SeriesProfile | null>(null);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    axios
      .get<SeriesProfile>("/client/series", {
        headers: {
          "X-SERIES-ID": id,
        },
      })
      .then((response) => {
        setSeries(response.data);
      })
      .catch(handleResponse)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (!series) {
    return (
      <LoadingContainer>
        <p>Series not found</p>
      </LoadingContainer>
    );
  }

  // Create tabs for each season
  const seasonTabs = series.seasons?.map((season, seasonIndex) => ({
    key: String(seasonIndex + 1),
    label: season.season_title || `Season ${season.season_number}`,
    children: (
      <Row style={{ width: "100%", flexWrap: "wrap", gap: "1rem" }}>
        {season.episodes?.map((episode, episodeIndex) => (
          <Card
            key={`${seasonIndex}-${episodeIndex}`}
            style={{ minWidth: "30rem", maxWidth: "30rem" }}
            title={`Episode ${episode.episode_number}${
              episode.title ? ` - ${episode.title}` : ""
            }`}
            size="small"
          >
            <Spacer size={-5} direction="bottom" />
            <Row style={{ gap: "1rem" }}>
              <Pill
                style={{
                  backgroundColor: colors.primary.hex,
                  width: "fit-content",
                  color: colors.white.hex,
                  fontWeight: 600,
                  textWrap: "nowrap",
                }}
              >
                {season.season_title || `Season ${season.season_number}`}
              </Pill>
              <Synopsis style={{ color: colors.text.hex, fontSize: "1.3rem" }}>
                {_.truncate(
                  (episode as any).synopsis || "No description available.",
                  { length: 75 }
                )}
              </Synopsis>
            </Row>
          </Card>
        ))}
        {(!season.episodes || season.episodes.length === 0) && (
          <Synopsis>No episodes available for this season.</Synopsis>
        )}
      </Row>
    ),
  })) || [];

  return (
    <ViewLayout
      id={id ?? ""}
      kind={ProjectKind.Series}
      showEpisodes={false}
      title="Episode List"
      subtitle={series.title || "Untitled Series"}
    >
      {seasonTabs.length > 0 ? (
        <Tabs items={seasonTabs} />
      ) : (
        <Synopsis>No seasons available.</Synopsis>
      )}
    </ViewLayout>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background-color: ${colors.lightBackground.hex};
  color: ${colors.text.hex};
`;

const Synopsis = styled.p`
  font-size: 1.5rem;
  color: ${colors.white.hex};
  font-weight: 400;
`;
